import React, { useContext } from 'react'
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material'
import { format, add } from 'date-fns'
import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'
import PageHeader from '../components/PageHeader'
import { UserContext } from '../context'

// Exact 365:230 capture box in px (avoids Safari rem rounding skewing html2canvas aspect ratio).
const PDF_CARD_CAPTURE_W_PX = 1012
const PDF_CARD_CAPTURE_H_PX = Math.round((PDF_CARD_CAPTURE_W_PX * 230) / 365)

const MemberCard = () => {
  const { user } = useContext(UserContext)

  // PDF generation function - can be called manually if needed
  const generatePdf = () => {
    // Portrait A4: on iPhone the PDF viewer is usually portrait, so the card fills the page
    // instead of appearing as a thin landscape strip with huge empty margins.
    const cardAsPdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
    const element = document.getElementById('testId') as HTMLElement
    if (!element) {
      return
    }

    const originalTransform = element.style.transform
    const originalWidth = element.style.width
    const originalHeight = element.style.height
    const originalAspectRatio = element.style.aspectRatio
    const originalOverflow = element.style.overflow

    const restoreDom = () => {
      element.style.transform = originalTransform
      element.style.aspectRatio = originalAspectRatio
      element.style.width = originalWidth
      element.style.height = originalHeight
      element.style.overflow = originalOverflow
    }

    element.style.transform = 'none'
    element.style.aspectRatio = 'unset'
    element.style.width = `${PDF_CARD_CAPTURE_W_PX}px`
    element.style.height = `${PDF_CARD_CAPTURE_H_PX}px`
    element.style.overflow = 'hidden'
    void element.offsetHeight

    html2canvas(element, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      width: PDF_CARD_CAPTURE_W_PX,
      height: PDF_CARD_CAPTURE_H_PX,
      windowWidth: PDF_CARD_CAPTURE_W_PX,
      windowHeight: PDF_CARD_CAPTURE_H_PX,
      scrollX: 0,
      scrollY: 0,
      backgroundColor: '#ffffff',
      onclone: (_document, cloned) => {
        cloned.style.overflow = 'hidden'
        cloned.style.boxSizing = 'border-box'
        cloned.style.width = `${PDF_CARD_CAPTURE_W_PX}px`
        cloned.style.height = `${PDF_CARD_CAPTURE_H_PX}px`
        cloned.style.backgroundSize = 'contain'
        cloned.style.backgroundPosition = 'center'
      }
    })
      .then(canvas => {
        restoreDom()

        const imageData = canvas.toDataURL('image/png', 1.0)

        const pdfWidth = 210
        const pdfHeight = 297
        const margin = 10
        const availableWidth = pdfWidth - margin * 2
        const availableHeight = pdfHeight - margin * 2

        const cardRatio = canvas.width / canvas.height
        const availableRatio = availableWidth / availableHeight
        let finalWidth: number
        let finalHeight: number
        if (cardRatio > availableRatio) {
          finalWidth = availableWidth
          finalHeight = availableWidth / cardRatio
        } else {
          finalHeight = availableHeight
          finalWidth = availableHeight * cardRatio
        }

        const xPos = margin + (availableWidth - finalWidth) / 2
        const yPos = margin + (availableHeight - finalHeight) / 2
        cardAsPdf.addImage(imageData, 'PNG', xPos, yPos, finalWidth, finalHeight)
        cardAsPdf.save('jasenkortti.pdf')
      })
      .catch(() => {
        restoreDom()
      })
  }

  if (!user) {
    return <CircularProgress />
  } else {
    const { qualifications } = user

    const currentDate = new Date()
    const membershipValidUntil = add(currentDate, { years: 1 })
    const formattedValidDate = format(membershipValidUntil, 'yyyy')
    const birthDate = new Date(user?.birthdate)
    const formattedBirthday = format(birthDate, 'dd.MM.yyyy')
    const mailingLanguage = user?.postikieli?.value

    // Check if user is a student member by looking at the actual membership type
    const checkIfStudentMember = () => {
      if (!user || !Array.isArray(user.memberships)) {
        return false
      }
      const currentDateISO = new Date().toISOString()
      const currentMembership = user.memberships.find(
        (m: any) =>
          m.yhdistys_name === 'Suomen Lääkäriliitto' &&
          m.els_jasenalkupvm &&
          m.els_jasenalkupvm < currentDateISO &&
          (!m.els_jasenloppu || m.els_jasenloppu > currentDateISO)
      )
      if (currentMembership?.els_jasentyyppi3_els_jasentyyppi) {
        const membershipType = currentMembership.els_jasentyyppi3_els_jasentyyppi
        // Check if membership type contains "Opiskelijajäsen" or "Opiskelija"
        return membershipType.includes('Opiskelijajäsen') || membershipType.includes('Opiskelija')
      }
      return false
    }

    const isStudentMember = checkIfStudentMember()
    const isNormalMember = !isStudentMember
    const specialities = qualifications?.filter(
      (q: any) => q?.tutkinnon_ylaluokka === 'Erikoislääkärin tutkinto'
    )

    // Get membership type word based on membership type and language
    const getMembershipTypeWord = () => {
      const isFinnish = mailingLanguage === 'Suomi'
      const isSwedish = mailingLanguage === 'Ruotsi' || mailingLanguage === 'Svenska'

      if (isStudentMember) {
        if (isFinnish) {
          return 'opiskelijajäsen'
        } else if (isSwedish) {
          return 'studentmedlem'
        } else {
          return 'student member'
        }
      } else {
        if (isFinnish) {
          return 'jäsen'
        } else if (isSwedish) {
          return 'medlem'
        } else {
          return 'member'
        }
      }
    }

    const membershipTypeWord = getMembershipTypeWord()

    return (
      <Box
        sx={{
          minHeight: '100dvh',
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          boxSizing: 'border-box'
        }}>
        <PageHeader />
        <Box
          component='main'
          sx={{
            flex: '1 1 auto',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            px: 2,
            py: 3,
            boxSizing: 'border-box'
          }}>
          <Stack
            id='testId'
            sx={{
              width: '100%',
              maxWidth: { xs: '100%', sm: '548px', md: '63.3rem' },
              aspectRatio: '365 / 230',
              height: 'auto',
              margin: '0 auto',
              backgroundImage: isNormalMember
                ? `url(${process.env.PUBLIC_URL}/jasenkortti.png)`
                : `url(${process.env.PUBLIC_URL}/jasenkortti-opiskelija.png)`,
              backgroundSize: 'contain',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center'
            }}>
            <Stack direction='row'>
              <Stack
                spacing={{ xs: 0.3, md: 1 }}
                sx={{
                  pl: { xs: '1rem', sm: '1.5rem', md: '3rem' },
                  pt: { xs: '5rem', sm: '7.5rem', md: '16rem' }
                }}>
                <Typography
                  fontSize={{
                    xs: 10,
                    sm: 15,
                    md: 26
                  }}>
                  {`Jäsennumero: ${user?.els_henkilonumero}`}
                </Typography>
                <Typography
                  fontSize={{
                    xs: 10,
                    sm: 15,
                    md: 26
                  }}>
                  {`Nimi: ${user?.firstname} ${user?.lastname}`}
                </Typography>
                <Typography
                  fontSize={{
                    xs: 10,
                    sm: 15,
                    md: 26
                  }}>
                  {`Syntymäpäivä: ${formattedBirthday}`}
                </Typography>
                <Typography
                  fontSize={{
                    xs: 10,
                    sm: 15,
                    md: 26
                  }}>
                  {`Jäsenyys voimassa 03/${formattedValidDate}`}
                </Typography>
                <Typography
                  fontSize={{
                    xs: 10,
                    sm: 15,
                    md: 26
                  }}>
                  {`Card valid 03/${formattedValidDate}`}
                </Typography>
              </Stack>
              <Stack
                spacing={{ xs: 0.3, md: 1 }}
                sx={{
                  pl: { xs: '3rem', sm: '4.5rem', md: '10rem' },
                  pt: { xs: '3rem', sm: '4.5rem', md: '9rem' }
                }}>
                <Typography
                  fontSize={{
                    xs: 10,
                    sm: 15,
                    md: 26
                  }}>
                  {mailingLanguage === 'Suomi' ? 'Erikoisalat:' : 'Specialities:'}
                </Typography>
                {specialities?.map((speciality: any, i: number) => (
                  <Typography
                    key={i}
                    fontSize={{
                      xs: 10,
                      sm: 15,
                      md: 26
                    }}>
                    {speciality.tutkinto_laillistus}
                  </Typography>
                ))}
              </Stack>
            </Stack>
            <Stack
              spacing={0.1}
              sx={{
                pl: { xs: '1rem', sm: '1.5rem', md: '3rem' },
                pt: { xs: '0.5rem', sm: '1rem', md: '0.5rem' },
                pb: { xs: '1.5rem', sm: '2.5rem', md: '4rem' }
              }}>
              {mailingLanguage === 'Suomi' ? (
                <>
                  <Typography
                    fontSize={{
                      xs: 7,
                      sm: 10,
                      md: 20
                    }}>
                    {`Tällä kortilla voit todistaa olevasi Lääkäriliiton ${membershipTypeWord}.`}
                  </Typography>
                  <Typography
                    fontSize={{
                      xs: 7,
                      sm: 10,
                      md: 20
                    }}>
                    {isStudentMember
                      ? 'The cardholder is a student member of the Finnish Medical Association.'
                      : 'The cardholder is a medical doctor and a member of the Finnish Medical Association.'}
                  </Typography>
                </>
              ) : (
                <>
                  <Typography
                    fontSize={{
                      xs: 7,
                      sm: 10,
                      md: 20
                    }}>
                    {`Med detta kort kan du bevisa att du är ${membershipTypeWord} i Läkarförbundet.`}
                  </Typography>
                  <Typography
                    fontSize={{
                      xs: 7,
                      sm: 10,
                      md: 20
                    }}>
                    {isStudentMember
                      ? 'The cardholder is a student member of the Finnish Medical Association.'
                      : 'The cardholder is a medical doctor and a member of the Finnish Medical Association.'}
                  </Typography>
                </>
              )}
            </Stack>
          </Stack>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mt: 2,
              mb: 2,
              width: '100%'
            }}>
            <Button
              variant='contained'
              color='primary'
              onClick={generatePdf}
              sx={{
                fontSize: { xs: '0.875rem', md: '1rem' },
                padding: { xs: '0.5rem 1rem', md: '0.75rem 1.5rem' },
                borderRadius: '8px'
              }}>
              Lataa jäsenkortti PDF
            </Button>
          </Box>
        </Box>
      </Box>
    )
  }
}

export default MemberCard
